// src/hooks/useFirestoreChat.js
import { useState, useEffect, useRef } from "react";
import {
  collection, query, orderBy, onSnapshot,
  addDoc, serverTimestamp, doc, setDoc, getDoc, getDocs, where
} from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../context/AuthContext";
import { sendNotification } from "./useNotifications";

export function useFirestoreChat(chatId) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user, profile } = useAuth();
  const bottomRef = useRef(null);

  useEffect(() => {
    if (!chatId) return;
    const q = query(
      collection(db, "chats", chatId, "messages"),
      orderBy("createdAt", "asc")
    );
    const unsub = onSnapshot(q, (snap) => {
      setMessages(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
    }, (err) => {
      console.error("Chat listen error:", err);
      setLoading(false);
    });
    return unsub;
  }, [chatId]);

  async function sendMessage(text, media = null) {
    if (!text?.trim() && !media) return;
    if (!chatId || !user) return;

    await addDoc(collection(db, "chats", chatId, "messages"), {
      text: text || "",
      senderId: user.uid,
      senderName: profile?.name || user.displayName || "مجهول",
      senderRole: profile?.role || "parent",
      mediaUrl:   media?.url      || null,
      mediaType:  media?.mediaType || null,
      fileName:   media?.fileName  || null,
      createdAt: serverTimestamp(),
      read: false
    });

    // ── تحديد نوع المحادثة ──
    const chatType = chatId.startsWith("paid_")    ? "paid"
                   : chatId.startsWith("free_")    ? "free"
                   : chatId === "group_doctors"     ? "group"
                   : "free";

    await setDoc(doc(db, "chats", chatId), {
      lastMessage: text || (media ? "📎 مرفق" : ""),
      lastMessageAt: serverTimestamp(),
      type: chatType,
      participants: chatId.includes("_")
        ? [user.uid]
        : []
    }, { merge: true });

    // ── إرسال إشعار للطرف الآخر ──
    await notifyOtherParty(chatId, chatType, text, media);
  }

  async function notifyOtherParty(chatId, chatType, text, media) {
  try {
    const senderName = profile?.name || "شخص ما";
    const preview = text
      ? (text.length > 40 ? text.slice(0, 40) + "..." : text)
      : "📎 أرسل مرفقاً";

    if (chatType === "group") return;

    if (profile?.role === "doctor") {
      // الطبيب أرسل → أشعر الأب
      const parentUid = chatId
        .replace("paid_", "")
        .replace("free_", "")
        .split("_")[0];

      if (parentUid && parentUid !== user.uid) {
        await sendNotification(
          parentUid,
          `رسالة جديدة من د. ${senderName}: ${preview}`,
          "message"
        );
      }
    } else {
      // الأب أرسل → أشعر جميع الأطباء
      try {
        const q = query(
          collection(db, "users"),
          where("role", "==", "doctor")
        );
        const snap = await getDocs(q);

        if (snap.empty) {
          console.log("No doctors found");
          return;
        }

        await Promise.all(
          snap.docs.map(d =>
            sendNotification(
              d.id,
              `رسالة جديدة من ${senderName}: ${preview}`,
              "message"
            )
          )
        );
      } catch (e) {
        console.error("Failed to query doctors:", e);
      }
    }
  } catch (e) {
    console.error("Notification error:", e);
  }
}

  return { messages, loading, sendMessage, bottomRef };
}

export async function getOrCreateChat(uid1, uid2, type = "free") {
  const chatId = [uid1, uid2].sort().join("_");
  const chatRef = doc(db, "chats", chatId);
  const snap = await getDoc(chatRef);
  if (!snap.exists()) {
    await setDoc(chatRef, {
      participants: [uid1, uid2],
      type,
      createdAt: serverTimestamp(),
      lastMessage: "",
      lastMessageAt: serverTimestamp()
    });
  }
  return chatId;
}